import { useCallback, useMemo, useRef, useState } from 'react'
import {
  ActualWorkingHoursRow,
  ActualWorkingHoursSkelton,
  fetchActualWorkingHours,
  ActualWorkingHoursRowScheduleBody,
  ActualWorkingHoursRowActualBody,
  calculateWorkTime,
  ActualWorkingHoursRowProjectBody,
  StandardWorkingInfo,
  ActualWorkingHoursSummaryRow,
  ActualWorkingHoursSummaryRowProjectBody,
  calculateBreakTime,
  RequestStatusRowProjectBody,
  updateRequestBatch,
  TaskActualWorkRow,
  ActualWorkingHoursApprovalBatchInput,
  TaskActualWorkingHoursApproveRequestInput,
  ActualWorkingHoursRatioRow,
  RequestRatioRowProjectBody,
  RequestStatus,
  WorkingHoursType,
} from '../../ActualWorkingHours/ActualWorkingHours'
import { AgGridTreeHelper } from '../../../containers/BulkSheetView/lib/tree'
import _ from 'lodash'
import store from '../../../../store'
import { doNotRequireSave } from '../../../../store/requiredSaveData'
import { APIResponse } from '../../../../lib/commons/api'
import Auth from '../../../../lib/commons/auth'
import DateVO from '../../../../vo/DateVO'
import TaskActualWorkAPI, {
  TaskActualWorkHour,
} from '../../../../lib/functions/taskActualWork'
import { featureFlagService } from '../../../../utils/feature-flag'
import { calculateRatio } from '../../ActualWorkingHours/gridOptions'
import { convertDecimalHourToSeconds } from '../../../../utils/time'

export const useTaskActualWorkData = (projectUuid: string) => {
  const [summaryRow, setSummaryRow] = useState<ActualWorkingHoursSummaryRow>(
    new ActualWorkingHoursSummaryRow()
  )
  const [data, setData] = useState<TaskActualWorkRow[]>([])
  const [ratioRow, setRatioRow] = useState<ActualWorkingHoursRatioRow>(
    new ActualWorkingHoursRatioRow()
  )
  const originalData = useRef<
    Map<
      string,
      Pick<
        ActualWorkingHoursRowActualBody,
        'workingHoursType' | 'startTime' | 'endTime' | 'breakTime' | 'remarks'
      >
    >
  >(new Map())

  const fetchRecords = useCallback(
    async (
      userUuid: string,
      projectUuid: string,
      startDate: string,
      endDate: string
    ) => {
      setData([])
      const response = await fetchActualWorkingHours({
        projectUuid,
        userUuid,
        startDate,
        endDate,
      })
      const sources: ActualWorkingHoursSkelton[] = response.json

      const responseTaskActualHour =
        await TaskActualWorkAPI.getTaskActualWorkHours({
          projectUuid: undefined,
          userUuid,
          startDate,
          endDate,
        })
      const taskActualWork: TaskActualWorkHour[] = responseTaskActualHour.json
      const rows = sources
        .map(v =>
          AgGridTreeHelper.convert(
            v,
            (s: ActualWorkingHoursSkelton): TaskActualWorkRow => {
              const targetTaskActualWorks = taskActualWork.filter(
                actualWork => actualWork.actualDate === s.workDate
              )
              let totalHour = 0

              totalHour = targetTaskActualWorks.reduce(
                (acc, cur) => acc + convertDecimalHourToSeconds(cur.hour),
                0
              )

              const row: ActualWorkingHoursRow = {
                type: 'WORK_HOUR',
                uuid: s.uuid,
                date: s.workDate,
                schedule: s.schedule
                  ? new ActualWorkingHoursRowScheduleBody(s.schedule)
                  : undefined,
                actual: s.actual
                  ? new ActualWorkingHoursRowActualBody(s.actual)
                  : undefined,
                totalActualHour: totalHour,
                projectHours: targetTaskActualWorks.map(actualWork => {
                  const body = new ActualWorkingHoursRowProjectBody()
                  body.projectUuid = actualWork.projectUuid
                  body.actualHour = convertDecimalHourToSeconds(actualWork.hour)
                  return body
                }),
                projectRequestStatus: targetTaskActualWorks.map(
                  actualWorkStatus => {
                    const body = new RequestStatusRowProjectBody()
                    body.projectUuid = actualWorkStatus.projectUuid
                    body.requestStatus = actualWorkStatus.requestStatus
                    body.requestUuid = actualWorkStatus.requestUuid ?? undefined
                    body.proxyUser = actualWorkStatus.proxyUser
                    return body
                  }
                ),
                approveCheck: false,
                sentBackCheck: false,
              } as TaskActualWorkRow
              row.workingHoursType =
                row.actual?.workingHoursType ??
                row.schedule?.workingHoursType ??
                ''
              row.remarks = row.actual?.remarks ?? row.schedule?.dateName ?? ''
              return row
            }
          )
        )
        .flat()
      setData(rows)
      originalData.current.clear()

      let sumBreak = 0
      let sumWorked = 0
      rows
        .filter(row => !!row.actual)
        .forEach(row => {
          originalData.current.set(
            row.uuid,
            _.cloneDeep({
              workingHoursType: row.actual!.workingHoursType,
              startTime: row.actual!.startTime,
              endTime: row.actual!.endTime,
              breakTime: row.actual!.breakTime,
              remarks: row.actual!.remarks,
            })
          )
          sumBreak += calculateBreakTime(row)
          sumWorked += calculateWorkTime(row)
        })

      const projectHourMap = new Map<
        string,
        ActualWorkingHoursSummaryRowProjectBody
      >()
      rows.forEach(row => {
        row.projectHours?.forEach(projectHour => {
          const body = projectHourMap.has(projectHour.projectUuid)
            ? (projectHourMap.get(
                projectHour.projectUuid
              ) as ActualWorkingHoursSummaryRowProjectBody)
            : new ActualWorkingHoursSummaryRowProjectBody(
                projectHour.projectUuid
              )
          body.actualHour += projectHour.actualHour
          projectHourMap.set(projectHour.projectUuid, body)
        })
        return projectHourMap
      })

      const projectRequestMap = new Map<string, RequestRatioRowProjectBody>()

      rows.forEach(row => {
        row.projectRequestStatus?.forEach(projectStatus => {
          const body = projectRequestMap.has(projectStatus.projectUuid)
            ? (projectRequestMap.get(
                projectStatus.projectUuid
              ) as RequestRatioRowProjectBody)
            : new RequestRatioRowProjectBody(projectStatus.projectUuid)
          if (projectStatus.requestStatus !== undefined) {
            body.requestStatusConunt = (body.requestStatusConunt ?? 0) + 1
          }
          if (projectStatus.requestStatus === RequestStatus.APPROVED) {
            body.approvedStatusConunt = (body.approvedStatusConunt ?? 0) + 1
          }
          projectRequestMap.set(projectStatus.projectUuid, body)
        })
      })

      const sumRow = new ActualWorkingHoursSummaryRow()
      sumRow.breakTime = sumBreak
      sumRow.workHour = sumWorked
      sumRow.totalActualHour = rows.reduce((sum, row) => {
        return sum + (row.totalActualHour || 0)
      }, 0)
      sumRow.projectHours = Array.from(projectHourMap.values())
      setSummaryRow(sumRow)
      const ratioRow = new ActualWorkingHoursRatioRow()
      const totalHour = rows.reduce((sum, row) => {
        return sum + (row.totalActualHour || 0)
      }, 0)
      ratioRow.totalActualHour = totalHour
      ratioRow.totalActualHourRatio =
        sumWorked > 0 ? calculateRatio(totalHour, sumWorked / 3600) : 0
      ratioRow.projectHours = Array.from(projectHourMap.values())
      ratioRow.projectRequestStatus = Array.from(projectRequestMap.values())
      setRatioRow(ratioRow)
    },
    []
  )

  const refresh = useCallback(
    async (
      userUuid: string,
      projectUuid: string,
      startDate: string,
      endDate: string
    ) => {
      const formatStart = new DateVO(startDate).formatForApi()!
      const formatEnd = new DateVO(endDate).formatForApi()!
      await fetchRecords(userUuid, projectUuid, formatStart, formatEnd)
      store.dispatch(doNotRequireSave())
    },
    [fetchRecords]
  )

  const updateWorkingRequest = useCallback(
    async (rows: TaskActualWorkRow[]): Promise<APIResponse> => {
      const tenant = Auth.getCurrentTenant()
      const userUuid = tenant!.user!.uuid
      const batchInput: ActualWorkingHoursApprovalBatchInput = {
        approve: convertRow2RequestForTaskActualWork(
          rows.filter(row => row.approveCheck),
          userUuid
        ),
        sentBack: convertRow2RequestForTaskActualWork(
          rows.filter(row => row.sentBackCheck),
          userUuid
        ),
      }
      return updateRequestBatch(batchInput)
    },
    []
  )

  const saveRequest = useCallback(async (): Promise<APIResponse> => {
    if (!featureFlagService.isEnabled('task-actual-work-application')) {
      return {
        status: 200,
        json: {},
        hasError: false,
        hasWarning: false,
      }
    }
    const edited = data.filter(row => row.edited)
    const response = await updateWorkingRequest(edited)
    return response
  }, [data, updateWorkingRequest])

  const convertRow2RequestForTaskActualWork = (
    items: TaskActualWorkRow[],
    userUuid: string
  ): TaskActualWorkingHoursApproveRequestInput[] => {
    const projectUuid = store.getState().project.selected
    return items.map(row => {
      return {
        userUuid,
        projectUuid,
        requestUuid: row.projectRequestStatus?.find(
          status => status.projectUuid === projectUuid
        )?.requestUuid,
        date: row.date!,
      } as TaskActualWorkingHoursApproveRequestInput
    })
  }

  const getStandardWorkingInfo = useCallback(() => {
    const scheduleHour = data.map(value => {
      return value.schedule?.workHour ?? 0
    })
    const workDays = scheduleHour.reduce((prev, cur) => {
      return prev + (cur > 0 ? 1 : 0)
    }, 0)
    const rawStdHour = scheduleHour.reduce((prev, cur) => {
      return prev + cur
    }, 0)
    let sumTime = 0
    let sumDays = 0
    data.forEach(row => {
      sumTime += calculateWorkTime(row)
      if (!!row.actual && !!row.actual.startTime && !!row.actual.endTime) {
        sumDays++
      }
    })
    return {
      standardWorkDays: workDays,
      standardWorkHours: rawStdHour,
      totalWorkHours: sumTime,
      totalWorkDays: sumDays,
      diffHours: sumTime - rawStdHour * 3600,
      diffDays: sumDays - workDays,
    } as StandardWorkingInfo
  }, [data])

  const hasNoRequestToApproveOrSendBack = useMemo(() => {
    return (
      isValidApprove(data, projectUuid).length === 0 &&
      isValidSentBack(data, projectUuid).length === 0
    )
  }, [data, projectUuid])

  return {
    data,
    summaryRow,
    ratioRow,
    refresh,
    saveRequest,
    getStandardWorkingInfo,
    hasNoRequestToApproveOrSendBack,
  }
}
export const isValidApprove = (
  rows: TaskActualWorkRow[],
  projectUuid: string
): TaskActualWorkRow[] => {
  return rows.filter(row => {
    if (
      ![WorkingHoursType.Attendance, WorkingHoursType.PaidLeaveHalf].includes(
        row.workingHoursType as WorkingHoursType
      )
    ) {
      return false
    }
    if (!row.projectRequestStatus || row.projectRequestStatus.length === 0) {
      return false
    }
    return row.projectRequestStatus.some(
      status =>
        status.projectUuid === projectUuid &&
        status.requestStatus === RequestStatus.REQUESTED
    )
  })
}
export const isValidSentBack = (
  rows: TaskActualWorkRow[],
  projectUuid: string
): TaskActualWorkRow[] => {
  return rows.filter(row => {
    if (
      ![WorkingHoursType.Attendance, WorkingHoursType.PaidLeaveHalf].includes(
        row.workingHoursType as WorkingHoursType
      )
    ) {
      return false
    }
    if (!row.projectRequestStatus || row.projectRequestStatus.length === 0) {
      return false
    }
    return row.projectRequestStatus.some(
      status =>
        status.projectUuid === projectUuid &&
        (status.requestStatus === RequestStatus.REQUESTED ||
          status.requestStatus === RequestStatus.APPROVED)
    )
  })
}
